// Pure helpers for the VideoPlayer (PRD §9.5, B2 PR3 / B2c).
//
// Lives in its own file so VideoPlayer.tsx contains only the
// component export — keeps Vite's react-refresh happy and lets
// these functions be imported from tests / future call sites
// (annotation overlays in B2e will need the same frame math).

/**
 * Format a number of seconds as `HH:MM:SS.mmm`. Negatives and
 * non-finite inputs (NaN, ±Infinity) clamp to `00:00:00.000` so the
 * UI never renders a gibberish counter while the <video> element is
 * still loading metadata.
 */
export function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00:00.000';
  const total = Math.floor(seconds);
  const ms = Math.floor((seconds - total) * 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number, w = 2): string => n.toString().padStart(w, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
}

/**
 * Convert a wall-clock seconds value to an integer frame number.
 * Returns null when fps is unknown (or zero / negative) — callers
 * should fall back to timecode rendering and skip the per-frame
 * step affordance.
 */
export function frameAt(
  seconds: number,
  fps: number | null | undefined
): number | null {
  if (fps === null || fps === undefined || fps <= 0) return null;
  return Math.floor(seconds * fps);
}
